/**
 * 重置密码页面
 * 
 * 功能：
 * - 检测URL中是否有token（判断是请求重置还是设置新密码）
 * - 请求重置模式：输入邮箱 → 发送重置邮件
 * - 设置密码模式：输入新密码 → 更新密码
 * - 密码规则提示
 */

import { useState, useEffect, useCallback, type FormEvent } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import { Eye, EyeOff, Lock, Mail, AlertCircle, CheckCircle } from 'lucide-react'
import { useAuth } from '@/contexts/AuthContext'
import { supabase } from '@/lib/supabase'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import LoadingSpinner from '@/components/LoadingSpinner'

type PageMode = 'request' | 'update' | 'success'

export default function ResetPasswordPage() {
  const navigate = useNavigate()
  const location = useLocation()
  const { resetPassword, updatePassword } = useAuth()
  
  // 页面模式
  const [mode, setMode] = useState<PageMode>('request')
  
  // 表单状态
  const [email, setEmail] = useState('')
  const [newPassword, setNewPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [showPassword, setShowPassword] = useState(false)
  
  // UI 状态
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [successMessage, setSuccessMessage] = useState<string | null>(null)

  // 检查URL中是否有recovery token
  useEffect(() => {
    const checkRecoveryToken = async () => {
      try {
        // Supabase 会自动处理 URL 中的 hash 参数
        // 如果是从重置邮件链接过来的，会自动设置 session
        const { data: { session } } = await supabase.auth.getSession()
        
        // 检查 URL hash 中是否有 type=recovery
        const hashParams = new URLSearchParams(location.hash.substring(1))
        const type = hashParams.get('type')
        
        if (type === 'recovery' || (session && location.hash.includes('access_token'))) {
          // 有recovery token，进入更新密码模式
          setMode('update')
        } else {
          // 没有token，进入请求重置模式
          setMode('request')
        }
      } catch (err) {
        console.error('检查recovery token失败:', err)
        setMode('request')
      } finally {
        setIsLoading(false)
      }
    }

    checkRecoveryToken()
  }, [location])

  // 验证邮箱格式
  const isValidEmail = (email: string): boolean => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
    return emailRegex.test(email)
  }

  // 处理请求重置密码
  const handleRequestReset = useCallback(async (e: FormEvent) => {
    e.preventDefault()
    setError(null)
    setSuccessMessage(null)

    if (!email.trim()) {
      setError('请输入邮箱地址')
      return
    }
    if (!isValidEmail(email)) {
      setError('请输入有效的邮箱地址')
      return
    }

    setIsLoading(true)

    try {
      const { error: resetError } = await resetPassword(email)
      
      if (resetError) {
        setError(resetError.message)
      } else {
        setSuccessMessage('密码重置链接已发送到您的邮箱，请查收邮件并点击链接重置密码')
      }
    } catch (err) {
      setError('发送重置邮件失败，请重试')
    } finally {
      setIsLoading(false)
    }
  }, [email, resetPassword])

  // 处理更新密码
  const handleUpdatePassword = useCallback(async (e: FormEvent) => {
    e.preventDefault()
    setError(null)

    if (!newPassword) {
      setError('请输入新密码')
      return
    }
    if (newPassword.length < 6) {
      setError('密码至少需要6个字符')
      return
    }
    if (newPassword !== confirmPassword) {
      setError('两次输入的密码不一致')
      return
    }

    setIsLoading(true)

    try {
      const { error: updateError } = await updatePassword(newPassword)
      
      if (updateError) {
        setError(updateError.message)
      } else {
        setMode('success')
      }
    } catch (err) {
      setError('更新密码失败，请重试')
    } finally {
      setIsLoading(false)
    }
  }, [newPassword, confirmPassword, updatePassword])

  // 跳转到主页
  const handleGoToHome = () => {
    navigate('/monitor')
  }

  // 加载中
  if (isLoading && mode !== 'request' && mode !== 'update') {
    return <LoadingSpinner variant="fullscreen" text="检查中..." />
  }

  return (
    <div className="auth-page flex min-h-screen items-center justify-center bg-[var(--canvas-fog)] p-4">
      <div className="w-full max-w-md">
        <div className="console-panel p-6 shadow-[0_24px_70px_rgba(0,0,0,.58)] sm:p-8">
          {/* 成功模式 */}
          {mode === 'success' && (
            <div className="text-center">
              <div className="mx-auto w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mb-4">
                <CheckCircle className="w-8 h-8 text-green-600" />
              </div>
              <h2 className="text-2xl font-bold text-gray-900 mb-2">
                密码重置成功
              </h2>
              <p className="text-gray-600 mb-6">
                您的密码已更新，现在可以使用新密码登录了
              </p>
              <Button onClick={handleGoToHome} className="w-full">
                前往登录
              </Button>
            </div>
          )}

          {/* 请求重置模式 */}
          {mode === 'request' && (
            <>
              <div className="text-center mb-8">
                <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-[var(--panel-raised)]">
                  <Mail className="h-8 w-8 text-[var(--action-blue)]" />
                </div>
                <h2 className="text-2xl font-bold text-gray-900">
                  重置密码
                </h2>
                <p className="mt-2 text-sm text-gray-600">
                  输入您的邮箱地址，我们将发送密码重置链接
                </p>
              </div>

              {/* 密码规则提示 */}
              <div className="mb-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
                <p className="text-sm font-medium text-blue-800 mb-2">密码要求：</p>
                <ul className="text-sm text-blue-700 list-disc pl-4 space-y-1">
                  <li>至少 6 个字符</li>
                  <li>不允许特殊 Unicode 字符</li>
                </ul>
              </div>

              {error && (
                <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg flex items-start gap-3">
                  <AlertCircle className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
                  <p className="text-sm text-red-700">{error}</p>
                </div>
              )}

              {successMessage && (
                <div className="mb-6 p-4 bg-green-50 border border-green-200 rounded-lg flex items-start gap-3">
                  <CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" />
                  <p className="text-sm text-green-700">{successMessage}</p>
                </div>
              )}

              <form onSubmit={handleRequestReset}>
                <div className="mb-6">
                  <Label htmlFor="email" className="text-sm font-medium text-gray-700">
                    邮箱地址
                  </Label>
                  <div className="relative mt-1">
                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                    <Input
                      id="email"
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      placeholder="请输入注册邮箱"
                      className="pl-10"
                      disabled={isLoading}
                      autoComplete="email"
                      required
                    />
                  </div>
                </div>

                <Button
                  type="submit"
                  className="w-full h-11 text-base"
                  disabled={isLoading}
                >
                  {isLoading ? '发送中...' : '发送重置链接'}
                </Button>
              </form>

              <div className="mt-6 text-center">
                <button
                  type="button"
                  className="text-sm text-gray-600 hover:text-gray-800 focus:outline-none focus:underline"
                  onClick={handleGoToHome}
                >
                  ← 返回登录
                </button>
              </div>
            </>
          )}

          {/* 更新密码模式 */}
          {mode === 'update' && (
            <>
              <div className="text-center mb-8">
                <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-[var(--panel-raised)]">
                  <Lock className="h-8 w-8 text-[var(--action-blue)]" />
                </div>
                <h2 className="text-2xl font-bold text-gray-900">
                  设置新密码
                </h2>
                <p className="mt-2 text-sm text-gray-600">
                  请输入您的新密码
                </p>
              </div>

              {/* 密码规则提示 */}
              <div className="mb-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
                <p className="text-sm font-medium text-blue-800 mb-2">密码要求：</p>
                <ul className="text-sm text-blue-700 list-disc pl-4 space-y-1">
                  <li>至少 6 个字符</li>
                  <li>不允许特殊 Unicode 字符</li>
                </ul>
              </div>

              {error && (
                <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg flex items-start gap-3">
                  <AlertCircle className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
                  <p className="text-sm text-red-700">{error}</p>
                </div>
              )}

              <form onSubmit={handleUpdatePassword}>
                {/* 新密码 */}
                <div className="mb-4">
                  <Label htmlFor="newPassword" className="text-sm font-medium text-gray-700">
                    新密码
                  </Label>
                  <div className="relative mt-1">
                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                    <Input
                      id="newPassword"
                      type={showPassword ? 'text' : 'password'}
                      value={newPassword}
                      onChange={(e) => setNewPassword(e.target.value)}
                      placeholder="请输入新密码"
                      className="pl-10 pr-10"
                      disabled={isLoading}
                      autoComplete="new-password"
                      required
                    />
                    <button
                      type="button"
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 focus:outline-none"
                      onClick={() => setShowPassword(!showPassword)}
                      aria-label={showPassword ? '隐藏密码' : '显示密码'}
                    >
                      {showPassword ? (
                        <EyeOff className="w-5 h-5" />
                      ) : (
                        <Eye className="w-5 h-5" />
                      )}
                    </button>
                  </div>
                </div>

                {/* 确认密码 */}
                <div className="mb-6">
                  <Label htmlFor="confirmPassword" className="text-sm font-medium text-gray-700">
                    确认密码
                  </Label>
                  <div className="relative mt-1">
                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                    <Input
                      id="confirmPassword"
                      type={showPassword ? 'text' : 'password'}
                      value={confirmPassword}
                      onChange={(e) => setConfirmPassword(e.target.value)}
                      placeholder="请再次输入新密码"
                      className="pl-10"
                      disabled={isLoading}
                      autoComplete="new-password"
                      required
                    />
                  </div>
                  {confirmPassword && newPassword !== confirmPassword && (
                    <p className="mt-1 text-sm text-red-500">两次输入的密码不一致</p>
                  )}
                </div>

                <Button
                  type="submit"
                  className="w-full h-11 text-base"
                  disabled={isLoading || (confirmPassword !== '' && newPassword !== confirmPassword)}
                >
                  {isLoading ? '更新中...' : '更新密码'}
                </Button>
              </form>
            </>
          )}
        </div>
      </div>
    </div>
  )
}
